'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  COMMON_TIME_ZONES,
  formatOffsetLabel,
  formatZoneDate,
  formatZoneTime,
  localTimeZone,
  zoneLabel,
  zoneOffsetMinutes,
} from '@/lib/browser-time-tools-extra';

const DEFAULT_ZONES = ['America/Los_Angeles', 'America/New_York', 'Europe/London', 'Asia/Tokyo'];

export default function BrowserWorldClock() {
  const [now, setNow] = useState<Date | null>(null);
  const [zones, setZones] = useState<string[]>(DEFAULT_ZONES);
  const [addZone, setAddZone] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [hour12, setHour12] = useState(false);

  useEffect(() => {
    setNow(new Date());
    const local = localTimeZone();
    setZones((prev) => (prev.includes(local) ? prev : [local, ...prev]));
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const available = useMemo(
    () => COMMON_TIME_ZONES.filter((zone) => !zones.includes(zone.id)),
    [zones],
  );

  function handleAdd() {
    setError(null);
    if (!addZone) return;
    if (zones.includes(addZone)) {
      setError('That time zone is already on the board.');
      return;
    }
    setZones((prev) => [...prev, addZone]);
    setAddZone('');
  }

  function handleRemove(id: string) {
    setZones((prev) => prev.filter((zone) => zone !== id));
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">World clock</span>
        <h2 className="tool-live-title">World clock</h2>
        <p className="tool-live-subtitle">
          Live local time across cities and time zones, updating every second. Nothing is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Add a time zone</span>
              <select onChange={(event) => setAddZone(event.target.value)} value={addZone}>
                <option value="">Select a city…</option>
                {available.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>Clock format</span>
              <select
                onChange={(event) => setHour12(event.target.value === '12')}
                value={hour12 ? '12' : '24'}
              >
                <option value="24">24-hour</option>
                <option value="12">12-hour</option>
              </select>
            </label>
          </div>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!addZone}
              onClick={handleAdd}
              type="button"
            >
              Add zone
            </button>
          </div>
          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          <p className="tool-live-panel-note">
            Times are computed locally with your browser&apos;s time-zone database.
          </p>
        </section>

        <section className="tool-live-preview">
          <div style={{ display: 'grid', gap: 12 }}>
            {now === null ? (
              <p className="tool-live-panel-note">Loading live clocks…</p>
            ) : (
              zones.map((zone) => {
                const offset = zoneOffsetMinutes(now, zone);
                return (
                  <div
                    key={zone}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '12px 14px',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      borderRadius: 12,
                    }}
                  >
                    <div style={{ display: 'grid', gap: 2 }}>
                      <strong style={{ fontSize: 15 }}>{zoneLabel(zone)}</strong>
                      <span style={{ fontSize: 12, opacity: 0.7 }}>
                        {formatZoneDate(now, zone)} · {formatOffsetLabel(offset)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span
                        style={{
                          fontSize: 22,
                          fontVariantNumeric: 'tabular-nums',
                          fontWeight: 600,
                        }}
                      >
                        {formatZoneTime(now, zone, { hour12, seconds: true })}
                      </span>
                      <button
                        aria-label={`Remove ${zoneLabel(zone)}`}
                        className="button button-secondary"
                        onClick={() => handleRemove(zone)}
                        style={{ padding: '4px 10px' }}
                        type="button"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
